'use client'

import { Text } from '../../../atoms'
import { CheckmarkSmallIcon } from '../../../icons'
import { SelectOption as SelectOptionType, TextTypes } from '../../../../types'
import React, { ReactElement } from 'react'

export interface SelectOptionProps {
    option: SelectOptionType
    index: number
    listboxId: string
    isSelected: boolean
    isFocused: boolean
    onActivate: (value: string | number) => void
}

/**
 * Single option row in the listbox. Renders  role="option" with label, optional footer,
 * and checkmark when selected. Handles click and Enter/Space to activate.
 */
export const SelectOption = ({
    option,
    index,
    listboxId,
    isSelected,
    isFocused,
    onActivate,
}: SelectOptionProps): ReactElement => (
    <li
        id={`${listboxId}-option-${index}`}
        role="option"
        aria-selected={isSelected}
        aria-disabled={option.disabled}
        tabIndex={-1}
        onClick={() => {
            if (!option.disabled) onActivate(option.value!)
        }}
        onKeyDown={(keyboardEvent) => {
            if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
                keyboardEvent.preventDefault()
                if (!option.disabled) onActivate(option.value!)
            }
        }}
        className={`flex items-center justify-between p-3 break-all 
            ${option.disabled ? 'cursor-not-allowed text-text-disabled' : 'cursor-pointer hover:bg-shade-100'}
            ${option.disabled && isSelected ? 'bg-accent-opacity-100' : ''} 
            ${isSelected && !option.disabled ? 'bg-accent-opacity-200' : ''}
             ${isFocused ? 'ring-1 ring-inset ring-accent-600' : ''}`}
    >
        <div className="inline-flex flex-col grow min-w-0">
            <Text>{option.label}</Text>
            {option.footer && <Text type={TextTypes.SMALL_TEXT}>{option.footer}</Text>}
        </div>
        {isSelected && (
            <CheckmarkSmallIcon className={option.disabled ? 'text-text-disabled' : 'text-text-interactive'} />
        )}
    </li>
)
